import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthSession } from "@/hooks/useAuthSession";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { SocialLinks } from "@/components/auth/SocialLinks";
import { AuthForm } from "@/components/auth/AuthForm";
import { verifyTurnstileToken, logSecurityEvent } from "@/utils/securityUtils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Loader2, Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { motion } from "framer-motion";
import { validatePassword } from "@/components/auth/PasswordValidation";
import { PasswordStrengthMeter } from "@/components/auth/PasswordStrengthMeter";
import { getDeviceInfo, getDeviceFingerprint } from "@/utils/deviceInfo";
import { validateReferralCode, recordReferralClick } from "@/utils/AffiliateUtils";

// Universal function to get redirect base - ensure all URLs are consistent
const getRedirectBase = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Always force www prefix for consistency with Supabase site URL config
  if (hostname === 'edaptica.com') {
    return 'https://www.edaptica.com';
  }
  
  // For development or preview environments, use the current origin
  return window.location.origin;
};

// Helper function to set a cookie that works across subdomains
const setSecureCookie = (name: string, value: string, days = 30) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  
  // Get the base domain (e.g., edaptica.com from www.edaptica.com)
  let domain = window.location.hostname;
  if (domain.startsWith('www.')) {
    domain = domain.substring(4); // Remove 'www.'
  }
  
  // Only set domain parameter for production domains, not localhost or preview domains
  let domainParam = '';
  if (domain !== 'localhost' && !domain.includes('.lovableproject.com')) {
    domainParam = `domain=.${domain};`;
  }
  
  // Set a secure cookie that works across subdomains
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;${domainParam}SameSite=Lax`;
  
  console.log(`Cookie '${name}' set with domain param: '${domainParam}'`);
};

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthSession();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    securityQuestion: '',
    securityAnswer: '',
    turnstileToken: '',
    referralCode: '', // Fix: Add referralCode to formData
  });
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [passwordMismatch, setPasswordMismatch] = useState<boolean>(false);

  // Handle successful login
  const handleSuccessfulLogin = async (userId: string, sessionId: string) => {
    try {
      // Record the new session in the active_sessions table
      const deviceInfo = getDeviceInfo();
      
      await supabase
        .from('active_sessions')
        .insert({
          user_id: userId,
          session_id: sessionId,
          device_info: deviceInfo,
        });
      
      // Log successful login security event
      await logSecurityEvent(
        userId,
        'login_success', 
        'User successfully logged in',
        { deviceInfo }
      );
      
      toast.success("Successfully logged in!");
      navigate('/', { replace: true });
    } catch (error) {
      console.error("Error recording session:", error);
      // Still navigate to home even if session recording fails
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    // Check if the Auth page was loaded with a request to show the password reset form
    if (location.state?.requestPasswordReset) {
      setIsForgotPassword(true);
    }

    // Check for recovery hash/query params and redirect to reset password immediately
    const checkForPasswordReset = () => {
      // Check hash format (old)
      if (window.location.hash && window.location.hash.includes('type=recovery')) {
        console.log('Auth page detected recovery hash, redirecting to reset password page');
        navigate('/auth/reset-password', { replace: true });
        return true;
      }
      
      // Check query params format (new)
      const searchParams = new URLSearchParams(window.location.search);
      if ((searchParams.has('type') && searchParams.get('type') === 'recovery') || 
          searchParams.has('code')) {
        console.log('Auth page detected recovery query params, redirecting to reset password page');
        navigate('/auth/reset-password', { replace: true });
        return true;
      }
      
      return false;
    };
    
    // If password reset was detected, don't continue with auth checks
    if (checkForPasswordReset()) {
      return;
    }
    
    // Check if already authenticated
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Check password validation on change when in signup mode
  useEffect(() => {
    if (isSignUp && formData.password) {
      const message = validatePassword(formData.password);
      setValidationMessage(message);
    } else {
      setValidationMessage(null);
    }
  }, [formData.password, isSignUp]);

  // Check if passwords match in signup mode
  useEffect(() => {
    if (isSignUp && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setPasswordMismatch(true);
    } else {
      setPasswordMismatch(false);
    }
  }, [formData.password, formData.confirmPassword, isSignUp]);

  const validateForm = () => {
    if (!formData.email || (!isForgotPassword && !formData.password)) {
      toast.error("Please fill in all required fields");
      return false;
    }

    if (isSignUp) {
      if (!formData.fullName) {
        toast.error("Please enter your full name");
        return false;
      }
      
      // Use more robust password validation
      if (validationMessage) {
        toast.error(validationMessage);
        return false;
      }
      
      if (passwordMismatch) {
        toast.error("Passwords do not match");
        return false;
      }
    }

    return true;
  };

  const handleSignIn = async (email: string, password: string, turnstileToken: string, fingerprint?: string) => {
    setIsLoading(true);
    try {
      // Special bypass for admin@admin.com user
      const isAdminUser = email.trim().toLowerCase() === 'admin@admin.com';
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (error) {
        await logSecurityEvent(
          null,
          'login_failure', 
          'Login attempt failed',
          { email, error: error.message }
        );
        throw error;
      }
      
      // Use the handleSuccessfulLogin function
      if (data.session?.user.id && data.session?.access_token) {
        await handleSuccessfulLogin(data.session.user.id, data.session.access_token);
      } else {
        toast.success("Successfully logged in!");
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error("Sign in error:", error);
      if (error instanceof Error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Invalid email or password");
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error("Failed to sign in");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (formData: {
    email: string;
    password: string;
    fullName: string;
    securityQuestion: string;
    securityAnswer: string;
    turnstileToken: string;
    fingerprint: string;
    referralCode?: string;
  }) => {
    setIsLoading(true);
    try {
      // Get the redirect base for consistent URLs
      const redirectBase = getRedirectBase();
      
      // Include additional metadata for referral tracking
      const userMetadata: any = {
        full_name: formData.fullName.trim(),
        security_question: formData.securityQuestion,
        security_answer: formData.securityAnswer,
      };
      
      // Add referral code to metadata if provided
      if (formData.referralCode) {
        console.log('Processing signup with referral code:', formData.referralCode);
        // Normalize and validate the referral code
        const normalizedCode = formData.referralCode.toLowerCase().trim();
        
        // Validate the referral code before using it
        const isValidCode = await validateReferralCode(normalizedCode);
        if (isValidCode) {
          console.log('Referral code validation successful, adding to user metadata');
          userMetadata['referral_code'] = normalizedCode;
          
          // Log the successful referral
          await logSecurityEvent(
            null,
            'referral_used', 
            'Referral code used during signup',
            { referral_code: normalizedCode }
          );
        } else {
          // If code is invalid but was provided, inform the user
          console.error('Invalid referral code during signup:', normalizedCode);
          toast.warning("The referral code provided was invalid or expired. Your account will be created without a referral.");
          // We continue with signup but without the referral code
          await logSecurityEvent(
            null,
            'referral_invalid', 
            'Invalid referral code used during signup',
            { referral_code: normalizedCode }
          );
        }
      }

      console.log('Proceeding with signup, user metadata:', userMetadata);

      const { data, error } = await supabase.auth.signUp({
        email: formData.email.trim(),
        password: formData.password,
        options: {
          data: userMetadata,
          // Explicitly specify the callback URL for confirmation emails
          emailRedirectTo: `${redirectBase}/auth/callback`,
        },
      });

      if (error) {
        await logSecurityEvent(
          null,
          'signup_failure', 
          'Signup attempt failed',
          { email: formData.email, error: error.message }
        );
        throw error;
      }

      if (data?.user) {
        await logSecurityEvent(
          data.user.id,
          'signup_success', 
          'New user account created successfully',
          { email: formData.email }
        );
        
        // Track successful referral if a valid code was provided
        if (formData.referralCode && userMetadata['referral_code']) {
          // Record the referral attribution for analytics
          await recordReferralClick(formData.referralCode);
          
          toast.success("Account created successfully with referral code! Please check your email to confirm your account.");
        } else {
          toast.success("Account created successfully! Please check your email to confirm your account.");
        }
        
        setIsSignUp(false);
      }
    } catch (error) {
      console.error("Signup error:", error);
      if (error instanceof Error) {
        if (error.message.includes("User already registered")) {
          toast.error("This email is already registered. Please try signing in instead.");
          setIsSignUp(false);
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error("Failed to create account");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsLoading(true);
    try {
      // Simple direct approach - just set the redirectTo
      const redirectTo = `${getRedirectBase()}/auth/reset-password`;
      console.log(`Sending password reset email with redirect to: ${redirectTo}`);
      
      const { error } = await supabase.auth.resetPasswordForEmail(
        formData.email.trim(), 
        { redirectTo }
      );

      if (error) throw error;
      
      toast.success("If your email exists in our system, you will receive password reset instructions");
      setIsForgotPassword(false);
    } catch (error) {
      console.error("Password reset error:", error);
      // Don't reveal if email exists or not for security
      toast.success("If your email exists in our system, you will receive password reset instructions");
      setIsForgotPassword(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleDeleteAccountRequest = async () => {
    const email = prompt("Please enter your email to request account deletion:");
    if (!email) return;
    
    try {
      const { error } = await supabase.functions.invoke('request-account-deletion', {
        body: { email }
      });
      
      if (error) throw error;
      
      toast.success("Account deletion request received. We'll contact you shortly.");
    } catch (error) {
      console.error('Error requesting account deletion:', error);
      toast.error("Failed to submit deletion request. Please try again.");
    }
  };

  // Animation variants for form transitions
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Get device fingerprint
    const fingerprint = getDeviceFingerprint();
    
    // Use empty string for turnstile token since we've disabled it
    const emptyToken = "";
    
    if (isSignUp) {
      handleSignUp({
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
        securityQuestion: formData.securityQuestion,
        securityAnswer: formData.securityAnswer,
        turnstileToken: emptyToken,
        fingerprint,
        referralCode: formData.referralCode || undefined
      });
    } else {
      handleSignIn(formData.email, formData.password, emptyToken, fingerprint);
    }
  };

  const renderForm = () => {
    if (isForgotPassword) {
      return (
        <motion.form 
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          onSubmit={handleForgotPassword} 
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                required
                className="pl-10 bg-white/50 backdrop-blur-sm focus:ring-2 focus:ring-primary/50 transition-all"
                aria-describedby="email-description"
              />
            </div>
            <p id="email-description" className="text-xs text-muted-foreground">We'll send you instructions to reset your password</p>
          </div>

          <div className="flex flex-col gap-4">
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transition-all hover:shadow-md"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Send Reset Link
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => setIsForgotPassword(false)}
              disabled={isLoading}
              className="w-full hover:bg-gray-50 transition-colors"
            >
              Back to Sign In
            </Button>
          </div>
        </motion.form>
      );
    }

    return (
      <motion.form 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={formVariants}
        onSubmit={handleSubmit} 
        className="space-y-4"
      >
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
              required
              className="pl-10 bg-white/50 backdrop-blur-sm focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
        </div>

        {isSignUp && (
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                id="fullName"
                name="fullName"
                type="text"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleChange}
                disabled={isLoading}
                required
                className="pl-10 bg-white/50 backdrop-blur-sm focus:ring-2 focus:ring-primary/50 transition-all"
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              required
              className="pl-10 pr-10 bg-white/50 backdrop-blur-sm focus:ring-2 focus:ring-primary/50 transition-all"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
            </button>
          </div>
          
          {isSignUp && (
            <PasswordStrengthMeter 
              password={formData.password}
              validationMessage={validationMessage}
            />
          )}
        </div>

        {isSignUp && (
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isLoading}
                required
                className="pl-10 pr-10 bg-white/50 backdrop-blur-sm focus:ring-2 focus:ring-primary/50 transition-all"
              />
            </div>
            {passwordMismatch && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-4 mt-6">
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-[#FF9933] via-[#2F9FEB] to-[#138808] hover:opacity-90 transition-all hover:shadow-md animate-tricolor-pulse"
            disabled={isLoading || (isSignUp && (!!validationMessage || passwordMismatch))}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : isSignUp ? (
              "Sign Up"
            ) : (
              "Sign In"
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => setIsSignUp(!isSignUp)}
            disabled={isLoading}
            className="w-full hover:bg-gray-50 transition-colors"
          >
            {isSignUp
              ? "Already have an account? Sign In"
              : "Don't have an account? Sign Up"}
          </Button>

          {!isSignUp && (
            <Button
              type="button"
              variant="link"
              onClick={() => setIsForgotPassword(true)}
              disabled={isLoading}
              className="w-full text-blue-600"
            >
              Forgot Password?
            </Button>
          )}
        </div>
      </motion.form>
    );
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100 via-white to-blue-100 z-0">
        <div className="absolute inset-0 bg-grid-pattern opacity-30"></div>
      </div>
      
      {/* Content container */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full px-4 max-w-md"
      >
        <AuthHeader />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <Card className="w-full backdrop-blur-sm border-opacity-50 shadow-lg hover:shadow-xl transition-all">
            <CardHeader>
              <CardTitle className="text-center">
                {isForgotPassword ? "Reset Password" : isSignUp ? "Create Account" : "Welcome Back"}
              </CardTitle>
              <CardDescription className="text-center">
                {isForgotPassword
                  ? "Enter your email to receive password reset instructions"
                  : isSignUp
                    ? "Sign up to start your adaptive learning journey"
                    : "Sign in to continue your personalized learning experience"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isForgotPassword ? (
                <motion.form 
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={formVariants}
                  onSubmit={handleForgotPassword} 
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isLoading}
                        required
                        className="pl-10 bg-white/50 backdrop-blur-sm focus:ring-2 focus:ring-primary/50 transition-all"
                        aria-describedby="email-description"
                      />
                    </div>
                    <p id="email-description" className="text-xs text-muted-foreground">We'll send you instructions to reset your password</p>
                  </div>

                  <div className="flex flex-col gap-4">
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transition-all hover:shadow-md"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Send Reset Link
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsForgotPassword(false)}
                      disabled={isLoading}
                      className="w-full hover:bg-gray-50 transition-colors"
                    >
                      Back to Sign In
                    </Button>
                  </div>
                </motion.form>
              ) : (
                <AuthForm 
                  isSignUp={isSignUp}
                  isLoading={isLoading}
                  onSignIn={handleSignIn}
                  onSignUp={handleSignUp}
                  onResetPassword={() => setIsForgotPassword(true)}
                  setIsSignUp={setIsSignUp}
                />
              )}
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <div className="text-center">
                <button
                  onClick={handleDeleteAccountRequest}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Request Account Deletion
                </button>
              </div>
              <div className="text-center text-xs text-muted-foreground">
                <p>By continuing, you agree to our <a href="/terms-and-conditions" className="text-primary hover:underline">Terms</a> and <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a></p>
              </div>
            </CardFooter>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <SocialLinks />
        </motion.div>
      </motion.div>
    </div>
  );
}
